import React, { useContext, useEffect, useState } from 'react'
import {
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	GridItem,
	Heading,
	HStack,
	Icon,
	IconButton,
	Image,
	Link,
	SimpleGrid,
	Text,
	VStack,
	useClipboard,
	useColorMode
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { flushSync } from 'react-dom'
import {
	FiArrowLeft,
	FiArrowUpRight,
	FiCheck,
	FiChevronRight,
	FiCopy,
	FiCpu,
	FiDownload,
	FiExternalLink,
	FiGithub,
	FiLayers,
	FiLinkedin,
	FiMail,
	FiMapPin,
	FiMoon,
	FiPhone,
	FiSend,
	FiServer,
	FiSun,
	FiTarget,
	FiTerminal
} from 'react-icons/fi'
import { SiLeetcode } from 'react-icons/si'
import { useNavigate } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import { containerVariants, itemVariants, pageVariants } from '../config/animations'
import { getTokens } from '../config/colorTokens'
import loaderContext from '../context/loaderContext'
import recruiterModeContext from '../context/recruiterModeContext'
import { experience } from '../data/experience'
import socials from '../data/socials'

const MotionVStack = motion(VStack)
const MotionBox = motion(Box)

const resumeUrl = 'https://drive.google.com/uc?export=download&id=1GspiR7an2CgK1jM-ttdV2lHlfhtsYEHx'
const contactEmail = 'prasoonsoni.work@gmail.com'
const contactPhone = '+91-9530002468'
const linkedin = socials.find((social) => social.site === 'Linkedin')?.url
const github = socials.find((social) => social.site === 'Github')?.url
const leetcode = socials.find((social) => social.site === 'Leetcode')?.url
const colorModePreferenceKey = 'site-color-mode-preference'

const decisionPoints = [
	'Software Engineer with 2 years of experience designing scalable, event-driven microservices using Java, Spring Boot, and Kafka.',
	'Strong in system design, CI/CD pipelines, REST APIs, fault-tolerant architecture, and performance optimization.',
	'Builds AI-driven backend systems including multi-agent workflows, RAG pipelines, MCP integrations, and workflow automation.',
	'Led production reliability work that reduced incidents by 95%, cut manual release effort by 75%, and validated 50+ releases per month.',
	'Owns full-stack delivery across React.js dashboards, Spring Boot services, Node.js systems, MongoDB workflows, and OpenShift deployments.'
]

const metrics = [
	{ value: '95%', label: 'production incidents reduced', context: 'release readiness dashboard' },
	{ value: '80%', label: 'manual effort reduced', context: 'multi-agent release automation' },
	{ value: '5K+', label: 'monthly interviews handled', context: 'AI.HYR recruitment platform' },
	{ value: '90%+', label: 'test coverage achieved', context: 'quality and reliability uplift' }
]

const dossier = [
	{ label: 'Target', value: 'Software Engineer' },
	{ label: 'Specialty', value: 'Backend, microservices, AI systems' },
	{ label: 'Experience', value: '2 years' },
	{ label: 'Education', value: 'B.Tech CSE, VIT · 9.1 CGPA' },
	{ label: 'Location', value: 'Hyderabad, India' }
]

const opportunityStatus = {
	value: 'Open to opportunities',
	detail: 'Software engineering roles focused on backend, microservices, and AI systems'
}

const fitSignals = [
	{
		icon: FiLayers,
		title: 'Full stack product delivery',
		text: 'Ships React.js interfaces backed by Spring Boot or Node.js services, clean API contracts, and production-ready data flows.'
	},
	{
		icon: FiServer,
		title: 'Backend systems',
		text: 'Designs event-driven microservices, resilient REST APIs, distributed workflows, idempotency, retries, circuit breakers, and observability.'
	},
	{
		icon: FiCpu,
		title: 'AI systems',
		text: 'Builds multi-agent systems, RAG pipelines, MCP-backed workflows, LLM screening engines, rubric scoring, and automation platforms.'
	}
]

const stack = [
	{ label: 'Languages', value: 'Java, Go, Python, JavaScript/TypeScript, C/C++, SQL' },
	{ label: 'Frontend', value: 'React.js, responsive workflows, recruiter and developer dashboards' },
	{ label: 'Backend', value: 'Spring Boot, Node.js, Express.js, REST APIs, microservices' },
	{ label: 'Architecture', value: 'Event-driven architecture, distributed systems, Kafka, fault tolerance' },
	{ label: 'Data', value: 'MongoDB, MySQL, Redis, asynchronous processing' },
	{ label: 'Platform', value: 'OpenShift/Kubernetes, CI/CD, Harness, Git, Swagger/OpenAPI' },
	{ label: 'Testing & Reliability', value: 'JUnit, Mockito, Playwright, Karate, HyperExecute, Splunk, Grafana, quality gates' },
	{ label: 'AI Systems', value: 'Agentic AI, agentic workflows, RAG, MCP, LLM screening' }
]

const interviewAngles = [
	'System design: event-driven loan origination workflows with Spring Boot, Kafka, fraud detection, pricing, and prequalification.',
	'Engineering leadership: leading 6 engineers through a multi-agent release automation platform with governance and audit readiness.',
	'AI product architecture: scaling a 0-to-1 recruitment platform with RAG, LLM screening, async evaluation, and Judge0 coding tests.'
]

const resumeExperience = [
	{
		id: 'wells-fargo-se',
		role: 'Software Engineer',
		company: 'Wells Fargo',
		type: 'Full-time',
		location: 'Hyderabad, IN',
		date: 'Aug 2024 - Present',
		logo: experience.find((role) => role.company === 'Wells Fargo')?.logo,
		description: [
			'Led a team of 6 engineers to architect and deliver a multi-agent release automation platform, reducing manual effort by 80% and improving release governance, compliance, and audit readiness.',
			'Designed scalable event-driven microservices for PayOn Loan originations using Spring Boot and Kafka, powering intake, prequalification, fraud detection, and pricing workflows.',
			'Spearheaded resilient high-availability APIs using idempotency, retries, circuit breakers, and structured logging; orchestrated 4 downstream systems for enrichment, claim creation, and resolution.',
			'Migrated 7+ production services from PCF to OpenShift Kubernetes and improved test coverage from 50% to 90%+.'
		]
	},
	{
		id: 'wells-fargo-intern',
		role: 'Technology Analyst Intern',
		company: 'Wells Fargo',
		type: 'Internship',
		location: 'Hyderabad, IN',
		date: 'Feb 2024 - Jul 2024',
		logo: experience.find((role) => role.company === 'Wells Fargo')?.logo,
		description: [
			'Built a centralized release readiness dashboard with React.js and Spring Boot, used by 12+ teams to identify recurring production incidents and configuration drift.',
			'Integrated GitHub, Harness, UCD, and JIRA to automate release evidence gathering, config drift detection, ThreadFix checks, and Sonar quality gates.',
			'Validated 50+ releases per month, reducing production incidents by 95%, cutting manual effort by 75%, and accelerating release prep by 50%.'
		]
	},
	{
		id: 'aihyr',
		role: 'Founding Software Engineer Intern',
		company: 'AI.HYR',
		type: 'Internship',
		location: 'Remote',
		date: 'Aug 2023 - Feb 2024',
		logo: experience.find((role) => role.company === 'AI.HYR')?.logo,
		description: [
			'Built and scaled a 0-to-1 AI recruitment platform supporting 5000+ interviews and coding tests per month.',
			'Engineered a RAG pipeline for job description and interview generation plus resume shortlisting, reducing manual effort by 80%.',
			'Developed an LLM-powered screening engine with rubric-based scoring, cutting turnaround time by 40%, and built resilient React.js, Node.js, MongoDB systems with async processing and Judge0 coding tests.'
		]
	}
]

const selectedProof = [
	{
		label: 'Project',
		title: 'Atom Dev Shell',
		text: 'CLI-based developer utility built with Java, Spring Boot, and Spring Shell to streamline CR pipelines, Jira workflows, deployments, and performance test script generation. Reduced developer friction by 70-80%.'
	},
	{
		label: 'Award',
		title: 'Manager Spotlight Award',
		text: 'Recognized at Wells Fargo for leading end-to-end component migration, delivering high-impact results, and driving GenAI and Agentic AI initiatives across the team.'
	},
	{
		label: 'Achievement',
		title: 'National Level Winner',
		text: 'Smart India Hackathon 2022 winner for problem statement VS942, organized by the Government of India.'
	}
]

const getRecruiterTheme = (colorMode, t) => {
	const isDark = colorMode === 'dark'

	return {
		pageBg: isDark ? '#080A0F' : '#FAFAF7',
		panelBg: isDark ? 'rgba(13, 16, 24, 0.72)' : 'rgba(255, 255, 252, 0.78)',
		panelStrong: isDark ? '#0F131D' : '#FFFFFF',
		panelMuted: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(20, 24, 33, 0.035)',
		line: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(17, 24, 39, 0.1)',
		lineStrong: isDark ? 'rgba(255, 255, 255, 0.17)' : 'rgba(17, 24, 39, 0.18)',
		text: isDark ? '#F6F2EA' : '#111318',
		textSoft: isDark ? '#B7BBC6' : '#4D5562',
		textMuted: isDark ? '#7E8594' : '#777F8C',
		ink: isDark ? '#05060A' : '#FFFFFF',
		gold: '#C9A15B',
		teal: '#2B9C96',
		rose: '#D06B75',
		platinum: isDark ? '#E8E2D4' : '#2C2F35',
		surfaceSheen: isDark ? 'linear-gradient(135deg, rgba(255,255,255,0.095), rgba(255,255,255,0.025) 42%, rgba(201,161,91,0.08))' : 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,255,252,0.76) 44%, rgba(201,161,91,0.07))',
		accent: t.accent,
		shadow: isDark ? '0 28px 90px rgba(0, 0, 0, 0.42)' : '0 28px 90px rgba(31, 35, 28, 0.12)',
		softShadow: isDark ? '0 16px 48px rgba(0, 0, 0, 0.24)' : '0 16px 48px rgba(31, 35, 28, 0.08)',
		inset: isDark ? 'inset 0 1px 0 rgba(255, 255, 255, 0.08)' : 'inset 0 1px 0 rgba(255, 255, 255, 0.92)'
	}
}

const RecruiterMode = () => {
	const { colorMode, setColorMode } = useColorMode()
	const { setLoading } = useContext(loaderContext)
	const { setRecruiterMode } = useContext(recruiterModeContext)
	const navigate = useNavigate()
	const { onCopy } = useClipboard(contactEmail)
	const [emailCopied, setEmailCopied] = useState(false)
	const baseTokens = getTokens(colorMode)
	const rt = getRecruiterTheme(colorMode, baseTokens)
	const featuredExperience = resumeExperience

	useEffect(() => {
		setLoading(false)
	}, [setLoading])

	const copyEmail = () => {
		onCopy()
		setEmailCopied(true)
		window.setTimeout(() => setEmailCopied(false), 1400)
	}

	const returnToNormalMode = () => {
		setRecruiterMode(false)
		navigate('/')
	}

	const toggleTheme = () => {
		const nextMode = colorMode === 'dark' ? 'light' : 'dark'
		if (typeof window !== 'undefined') {
			window.localStorage.setItem(colorModePreferenceKey, nextMode)
		}

		if (typeof document === 'undefined' || !document.startViewTransition) {
			setColorMode(nextMode)
			return
		}

		document.startViewTransition(() => {
			flushSync(() => setColorMode(nextMode))
		})
	}

	return (
		<MotionVStack
			variants={pageVariants}
			initial="hidden"
			animate="visible"
			spacing={0}
			w="100%"
			minH="100vh"
			align="stretch"
			bg={rt.pageBg}
			color={rt.text}
			position="relative"
			overflow="hidden">
			<SEOHead
				title="Recruiter Brief | Prasoon Soni"
				description="Recruiter-focused profile for Prasoon Soni, a Software Engineer specializing in Java, Spring Boot, Kafka, event-driven microservices, CI/CD, and AI-driven backend systems."
				path="/recruiter"
			/>

			<Backdrop rt={rt} />

			<Box position="relative" zIndex={1}>
				<VStack align="stretch" w="100%" maxW="1240px" mx="auto" px={{ base: 4, md: 8 }} pt={{ base: 4, md: 6 }} pb={{ base: 12, md: 18 }} spacing={{ base: 6, md: 8 }}>
					<TopBar rt={rt} colorMode={colorMode} onToggleTheme={toggleTheme} onReturn={returnToNormalMode} />

					<Grid
						as={motion.div}
						variants={containerVariants}
						initial="hidden"
						animate="visible"
						templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
						gap={{ base: 5, lg: 6 }}
						alignItems="stretch">
						<GridItem as={MotionBox} variants={itemVariants}>
							<HeroBrief rt={rt} copied={emailCopied} onCopy={copyEmail} />
						</GridItem>
						<GridItem as={MotionBox} variants={itemVariants}>
							<CandidateCard rt={rt} />
						</GridItem>
					</Grid>

					<PremiumMetricStrip metrics={metrics} rt={rt} />
				</VStack>
			</Box>

			<Box position="relative" zIndex={1} bg={colorMode === 'dark' ? 'rgba(255,255,255,0.025)' : 'rgba(255,255,255,0.42)'} borderTop="1px solid" borderColor={rt.line}>
				<VStack align="stretch" w="100%" maxW="1240px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 10, md: 16 }} spacing={{ base: 10, md: 14 }}>
					<SectionBlock eyebrow="Screening fit" title="Software engineer for backend-heavy, AI-enabled systems." rt={rt}>
						<SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
							{fitSignals.map((signal) => (
								<SignalCard key={signal.title} signal={signal} rt={rt} />
							))}
						</SimpleGrid>
					</SectionBlock>

					<Grid templateColumns={{ base: '1fr', lg: '0.9fr 1.1fr' }} gap={{ base: 8, lg: 6 }} alignItems="start">
						<SectionBlock eyebrow="Technical surface" title="A resume-backed stack for scalable systems work." rt={rt}>
							<StackMatrix stack={stack} rt={rt} />
						</SectionBlock>
						<SectionBlock eyebrow="Evidence" title="Recent impact, trimmed for signal." rt={rt}>
							<ExperienceTimeline roles={featuredExperience} rt={rt} />
						</SectionBlock>
					</Grid>

					<Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
						<ProofPanel rt={rt} />
						<ContactPanel rt={rt} copied={emailCopied} onCopy={copyEmail} />
					</Grid>
				</VStack>
			</Box>
		</MotionVStack>
	)
}

const Backdrop = ({ rt }) => (
	<Box position="absolute" inset={0} pointerEvents="none" overflow="hidden">
		<Box
			position="absolute"
			inset={0}
			opacity={0.5}
			bgImage={`linear-gradient(90deg, ${rt.line} 1px, transparent 1px), linear-gradient(0deg, ${rt.line} 1px, transparent 1px)`}
			bgSize="82px 82px, 82px 82px"
			maskImage="linear-gradient(to bottom, black 0%, black 58%, transparent 100%)"
		/>
		<Box position="absolute" top="-8vh" left="0" right="0" h="48vh" bg="linear-gradient(180deg, rgba(201, 161, 91, 0.18), rgba(43, 156, 150, 0.08) 42%, transparent)" />
		<Box position="absolute" top="118px" left="50%" transform="translateX(-50%)" w="min(920px, 82vw)" h="1px" bg={`linear-gradient(90deg, transparent, ${rt.lineStrong}, transparent)`} />
	</Box>
)

const TopBar = ({ rt, colorMode, onToggleTheme, onReturn }) => (
	<Flex
		align="center"
		justify="space-between"
		gap={{ base: 2, md: 4 }}
		position="sticky"
		top={{ base: '8px', md: '16px' }}
		zIndex={20}
		px={{ base: 2.5, md: 4 }}
		py={{ base: 2.5, md: 3 }}
		bg={rt.panelBg}
		border="1px solid"
		borderColor={rt.line}
		borderRadius="8px"
		backdropFilter="blur(22px)"
		boxShadow={`${rt.softShadow}, ${rt.inset}`}>
		<HStack minW="0" spacing={{ base: 2.5, md: 3 }}>
			<Flex align="center" justify="center" w={{ base: '34px', md: '36px' }} h={{ base: '34px', md: '36px' }} borderRadius="8px" bg={rt.text} color={rt.ink} boxShadow={rt.inset} flex="0 0 auto">
				<Icon as={FiTarget} />
			</Flex>
			<Box minW="0">
				<Text color={rt.text} fontSize={{ base: 'xs', sm: 'sm' }} fontWeight="800" noOfLines={1}>
					Recruiter brief
				</Text>
				<Text color={rt.textMuted} fontSize={{ base: '11px', sm: 'xs' }} noOfLines={1}>
					Prasoon Soni · Backend + AI
				</Text>
			</Box>
		</HStack>
		<HStack spacing={{ base: 1.5, md: 2 }} flex="0 0 auto">
			<IconButton
				aria-label={`Switch to ${colorMode === 'dark' ? 'light' : 'dark'} theme`}
				icon={<Icon as={colorMode === 'dark' ? FiSun : FiMoon} />}
				size="sm"
				borderRadius="8px"
				minW={{ base: '34px', md: '40px' }}
				h={{ base: '34px', md: '32px' }}
				bg={rt.panelMuted}
				color={rt.text}
				border="1px solid"
				borderColor={rt.lineStrong}
				onClick={onToggleTheme}
				_hover={{ bg: 'transparent', borderColor: rt.gold }}
			/>
			<IconButton
				display={{ base: 'inline-flex', md: 'none' }}
				aria-label="Back to portfolio"
				icon={<FiArrowLeft />}
				size="sm"
				borderRadius="8px"
				minW="34px"
				h="34px"
				bg="transparent"
				color={rt.text}
				border="1px solid"
				borderColor={rt.lineStrong}
				onClick={onReturn}
				_hover={{ bg: rt.panelMuted }}
			/>
			<Button
				display={{ base: 'none', md: 'inline-flex' }}
				leftIcon={<FiArrowLeft />}
				size="sm"
				borderRadius="8px"
				h="32px"
				px={3}
				bg="transparent"
				color={rt.text}
				border="1px solid"
				borderColor={rt.lineStrong}
				onClick={onReturn}
				_hover={{ bg: rt.panelMuted }}>
				Portfolio
			</Button>
		</HStack>
	</Flex>
)

const HeroBrief = ({ rt, copied, onCopy }) => (
	<VStack
		align="stretch"
		justify="space-between"
		minH={{ base: 'auto', lg: '620px' }}
		h="100%"
		spacing={{ base: 7, md: 8 }}
		p={{ base: 5, md: 8 }}
		bg={rt.panelBg}
		border="1px solid"
		borderColor={rt.line}
		borderRadius="8px"
		backdropFilter="blur(24px)"
		boxShadow={`${rt.shadow}, ${rt.inset}`}
		overflow="hidden"
		position="relative">
		<Box position="absolute" inset={0} bg={rt.surfaceSheen} pointerEvents="none" />
		<Box
			position="absolute"
			top="0"
			left="0"
			right="0"
			h="3px"
			bg={`linear-gradient(90deg, ${rt.gold}, ${rt.teal}, ${rt.lineStrong}, ${rt.gold})`}
			bgSize="220% 100%"
			animation="recruiter-gradient-flow 5.5s ease-in-out infinite"
			sx={{
				'@keyframes recruiter-gradient-flow': {
					'0%, 100%': { backgroundPosition: '0% 50%' },
					'50%': { backgroundPosition: '100% 50%' }
				},
				'@media (prefers-reduced-motion: reduce)': {
					animation: 'none'
				}
			}}
		/>
		<VStack align="start" spacing={{ base: 6, md: 7 }} position="relative">
			<MetaLine rt={rt} items={['Software Engineer', 'Java · Spring Boot · Kafka', 'AI backend systems', 'Hyderabad · India']} />

			<Box>
				<Text color={rt.gold} fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em" mb={4}>
					Candidate snapshot
				</Text>
				<Heading
					as="h1"
					color={rt.text}
					fontSize={{ base: '46px', sm: '62px', md: '82px' }}
					fontWeight="800"
					lineHeight="0.94"
					letterSpacing="0">
					Prasoon Soni
				</Heading>
				<Text color={rt.textSoft} fontSize={{ base: 'lg', md: '2xl' }} lineHeight="1.45" maxW="720px" mt={6}>
					Software Engineer designing event-driven microservices, resilient APIs, CI/CD systems, and AI-driven backend workflows.
				</Text>
			</Box>

			<VStack align="stretch" spacing={0} w="100%" borderTop="1px solid" borderColor={rt.line}>
				{decisionPoints.map((point) => (
					<Flex key={point} align="start" gap={3} py={3} borderBottom="1px solid" borderColor={rt.line}>
						<Text color={rt.gold} fontSize="xs" fontWeight="800" lineHeight="1.8" flex="0 0 auto">
							/
						</Text>
						<Text color={rt.textSoft} fontSize={{ base: 'sm', md: 'md' }} lineHeight="1.6">
							{point}
						</Text>
					</Flex>
				))}
			</VStack>
		</VStack>

		<SimpleGrid columns={{ base: 2, sm: 3 }} gap={3} position="relative">
			<Button as={Link} href={`mailto:${contactEmail}`} leftIcon={<FiSend />} borderRadius="8px" h="48px" w="100%" bg={rt.text} color={rt.ink} _hover={{ bg: rt.gold, textDecoration: 'none' }}>
				Email Prasoon
			</Button>
			<Button as={Link} href={resumeUrl} target="_blank" rightIcon={<FiExternalLink />} borderRadius="8px" h="48px" w="100%" bg={rt.panelMuted} color={rt.text} border="1px solid" borderColor={rt.line} _hover={{ bg: 'transparent', borderColor: rt.gold, textDecoration: 'none' }}>
				View resume
			</Button>
			<Box gridColumn={{ base: 'span 2', sm: 'auto' }}>
				<CopyEmailButton copied={copied} onCopy={onCopy} rt={rt} h="48px" />
			</Box>
		</SimpleGrid>
	</VStack>
)

const CopyEmailButton = ({ copied, onCopy, rt, h = '40px' }) => (
	<Button
		aria-label={copied ? 'Email copied' : 'Copy email'}
		leftIcon={<Icon as={copied ? FiCheck : FiCopy} />}
		borderRadius="8px"
		h={h}
		w="100%"
		px={4}
		bg={rt.panelMuted}
		color={copied ? rt.teal : rt.textSoft}
		border="1px solid"
		borderColor={copied ? rt.teal : rt.line}
		onClick={onCopy}
		_hover={{ color: rt.text, borderColor: rt.teal }}
		sx={{
			'.chakra-button__icon': {
				marginInlineEnd: '0.5rem'
			}
		}}>
		<Text as="span">
			{copied ? 'Email copied' : 'Copy email'}
		</Text>
	</Button>
)

const CandidateCard = ({ rt }) => (
	<VStack align="stretch" spacing={4} h="100%" minH={{ base: 'auto', lg: '620px' }} p={{ base: 4, md: 5 }} bg={rt.panelBg} border="1px solid" borderColor={rt.line} borderRadius="8px" boxShadow={`${rt.shadow}, ${rt.inset}`} backdropFilter="blur(24px)" overflow="hidden">
		<Box position="relative" flex="1" minH={{ base: '360px', md: '440px', lg: '0' }} borderRadius="8px" overflow="hidden" border="1px solid" borderColor={rt.line}>
			<Box position="absolute" inset="10px" border="1px solid" borderColor="whiteAlpha.300" borderRadius="6px" zIndex={2} pointerEvents="none" />
			<Image src="/profile.png" alt="Prasoon Soni" w="100%" h="100%" objectFit="cover" filter="saturate(0.92) contrast(1.04)" />
			<Box position="absolute" inset={0} bg="linear-gradient(to top, rgba(0,0,0,0.68), rgba(0,0,0,0.04) 54%, rgba(0,0,0,0.12))" />
			<VStack position="absolute" left={0} right={0} bottom={0} align="stretch" p={{ base: 5, md: 6 }} spacing={4} zIndex={3}>
				<HStack justify="space-between" align="end">
					<Box>
						<Text color="whiteAlpha.800" fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em">
							Primary lane
						</Text>
						<Heading color="white" fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1.05" mt={2}>
							Software Engineer
						</Heading>
					</Box>
					<Flex align="center" gap={1.5} color="whiteAlpha.800" fontSize="sm">
						<Icon as={FiMapPin} />
						<Text>Hyderabad</Text>
					</Flex>
				</HStack>
			</VStack>
		</Box>

		<OpportunityStatusChip rt={rt} />
		<DossierCard rt={rt} embedded />
	</VStack>
)

const OpportunityStatusChip = ({ rt }) => (
	<Flex align="center" gap={3} px={4} py={3} borderRadius="8px" bg={rt.panelMuted} color={rt.text} border="1px solid" borderColor={rt.lineStrong}>
		<Box w="8px" h="8px" borderRadius="full" bg={rt.teal} boxShadow={`0 0 0 4px rgba(43, 156, 150, 0.2)`} flex="0 0 auto" />
		<Box minW="0">
			<Text fontSize="sm" fontWeight="800" lineHeight="1.25">
				{opportunityStatus.value}
			</Text>
			<Text color={rt.textMuted} fontSize="xs" lineHeight="1.45" mt={1}>
				{opportunityStatus.detail}
			</Text>
		</Box>
	</Flex>
)

const MetaLine = ({ items, rt }) => (
	<Flex align="center" gap={{ base: 2, md: 3 }} wrap="wrap" color={rt.textMuted} fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">
		{items.map((item, index) => (
			<HStack key={item} spacing={{ base: 2, md: 3 }}>
				{index > 0 && <Box w="4px" h="4px" borderRadius="full" bg={rt.lineStrong} />}
				<Text color={index === 0 ? rt.gold : rt.textMuted}>{item}</Text>
			</HStack>
		))}
	</Flex>
)

const DossierCard = ({ rt, embedded = false }) => (
	<VStack align="stretch" spacing={0} bg={embedded ? 'transparent' : rt.panelBg} border="1px solid" borderColor={rt.line} borderRadius="8px" overflow="hidden" boxShadow={embedded ? 'none' : rt.inset} backdropFilter={embedded ? undefined : 'blur(18px)'}>
		<Flex align="center" justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor={rt.line}>
			<Text color={rt.gold} fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.12em">
				Quick facts
			</Text>
			<Text color={rt.textMuted} fontSize="xs" fontWeight="800">
				Resume aligned
			</Text>
		</Flex>
		{dossier.map((item) => (
			<Flex key={item.label} align="start" justify="space-between" gap={4} px={4} py={2.5} borderBottom="1px solid" borderColor={rt.line} _last={{ borderBottom: '0' }}>
				<Text color={rt.textMuted} fontSize="xs" fontWeight="800" textTransform="uppercase" flex="0 0 78px">
					{item.label}
				</Text>
				<Text color={rt.text} fontSize="sm" fontWeight="700" lineHeight="1.45" textAlign="right">
					{item.value}
				</Text>
			</Flex>
		))}
	</VStack>
)

const PremiumMetricStrip = ({ metrics, rt }) => (
	<SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={0} bg={rt.panelBg} border="1px solid" borderColor={rt.line} borderRadius="8px" overflow="hidden" boxShadow={`${rt.softShadow}, ${rt.inset}`} backdropFilter="blur(18px)">
		{metrics.map((metric, index) => (
			<Box key={metric.label} p={{ base: 5, md: 6 }} borderRight={{ base: '0', lg: index === metrics.length - 1 ? '0' : '1px solid' }} borderBottom={{ base: index === metrics.length - 1 ? '0' : '1px solid', sm: index > 1 ? '0' : '1px solid', lg: '0' }} borderColor={rt.line} position="relative">
				<Flex align="start" justify="space-between" gap={3}>
					<Box>
						<Text color={rt.text} fontSize={{ base: '4xl', md: '5xl' }} fontWeight="800" lineHeight="0.9">
							{metric.value}
						</Text>
						<Text color={rt.textSoft} fontSize="sm" fontWeight="800" mt={4}>
							{metric.label}
						</Text>
						<Text color={rt.textMuted} fontSize="xs" mt={1}>
							{metric.context}
						</Text>
					</Box>
					<Text color={index === 0 ? rt.gold : rt.textMuted} fontSize="xs" fontWeight="800">
						0{index + 1}
					</Text>
				</Flex>
			</Box>
		))}
	</SimpleGrid>
)

const SectionBlock = ({ eyebrow, title, children, rt }) => (
	<VStack align="stretch" spacing={5}>
		<Box>
			<Text color={rt.gold} fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em">
				{eyebrow}
			</Text>
			<Heading color={rt.text} fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" lineHeight="1.05" maxW="760px" mt={2}>
				{title}
			</Heading>
		</Box>
		{children}
	</VStack>
)

const SignalCard = ({ signal, rt }) => (
	<MotionBox variants={itemVariants} p={{ base: 5, md: 6 }} bg={rt.panelStrong} border="1px solid" borderColor={rt.line} borderRadius="8px" boxShadow={`${rt.softShadow}, ${rt.inset}`}>
		<Flex align="center" justify="center" w="44px" h="44px" borderRadius="8px" color={rt.ink} bg={rt.text} mb={5} boxShadow={rt.inset}>
			<Icon as={signal.icon} />
		</Flex>
		<Text color={rt.text} fontSize="xl" fontWeight="800">
			{signal.title}
		</Text>
		<Text color={rt.textSoft} fontSize="sm" lineHeight="1.75" mt={3}>
			{signal.text}
		</Text>
	</MotionBox>
)

const StackMatrix = ({ stack, rt }) => (
	<SimpleGrid columns={{ base: 1, sm: 2 }} gap={3}>
		{stack.map((group) => (
			<Box key={group.label} p={4} bg={rt.panelStrong} border="1px solid" borderColor={rt.line} borderRadius="8px">
				<HStack spacing={3} align="center">
					<Flex align="center" justify="center" w="34px" h="34px" borderRadius="8px" bg="rgba(216, 174, 94, 0.14)" color={rt.gold} flex="0 0 auto">
						<Icon as={FiTerminal} />
					</Flex>
					<Text color={rt.text} fontWeight="800">
						{group.label}
					</Text>
				</HStack>
				<Text color={rt.textSoft} fontSize="sm" lineHeight="1.65" mt={3}>
					{group.value}
				</Text>
			</Box>
		))}
	</SimpleGrid>
)

const ExperienceTimeline = ({ roles, rt }) => (
	<VStack align="stretch" spacing={0} bg={rt.panelStrong} border="1px solid" borderColor={rt.line} borderRadius="8px" overflow="hidden">
		{roles.map((role, index) => (
			<Box key={role.id} p={{ base: 5, md: 6 }} borderBottom={index === roles.length - 1 ? '0' : '1px solid'} borderColor={rt.line}>
				<Flex gap={4} align="start" justify="space-between" direction={{ base: 'column', sm: 'row' }}>
					<HStack align="start" spacing={3}>
						<Image src={role.logo} alt={`${role.company} logo`} w="42px" h="42px" borderRadius="8px" objectFit="cover" border="1px solid" borderColor={rt.lineStrong} />
						<Box>
							<Text color={rt.text} fontSize="lg" fontWeight="800" lineHeight="1.2">
								{role.role}
							</Text>
							<Text color={rt.textSoft} fontSize="sm" mt={1}>
								{role.company} · {role.type}
							</Text>
						</Box>
					</HStack>
					<Text color={rt.textMuted} fontSize="xs" fontWeight="bold" textTransform="uppercase" whiteSpace="nowrap">
						{role.date}
					</Text>
				</Flex>
				<VStack align="stretch" spacing={2.5} mt={4}>
					{role.description.slice(0, 2).map((item) => (
						<HStack key={item} align="start" spacing={3}>
							<Icon as={FiChevronRight} color={rt.teal} mt="4px" flex="0 0 auto" />
							<Text color={rt.textSoft} fontSize="sm" lineHeight="1.72">
								{item}
							</Text>
						</HStack>
					))}
				</VStack>
			</Box>
		))}
	</VStack>
)

const ProofPanel = ({ rt }) => (
	<VStack align="stretch" spacing={5} p={{ base: 5, md: 6 }} bg={rt.text} color={rt.ink} borderRadius="8px" minH="100%">
		<HStack justify="space-between" align="start">
			<Box>
				<Text color={rt.gold} fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em">
					Projects and achievements
				</Text>
				<Heading fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1.08" mt={2}>
					Extra proof from the resume.
				</Heading>
			</Box>
			<Icon as={FiArrowUpRight} fontSize="28px" />
		</HStack>
		<Divider borderColor={rt.ink === '#FFFFFF' ? 'whiteAlpha.300' : 'blackAlpha.300'} />
		<VStack align="stretch" spacing={3}>
			{selectedProof.map((item) => (
				<HStack key={item.title} align="start" spacing={3}>
					<Flex align="center" justify="center" w="24px" h="24px" borderRadius="6px" bg={rt.ink === '#FFFFFF' ? 'whiteAlpha.200' : 'blackAlpha.100'} flex="0 0 auto">
						<Icon as={FiCheck} fontSize="13px" />
					</Flex>
					<Box>
						<Text fontSize="xs" fontWeight="800" textTransform="uppercase" opacity={0.62}>
							{item.label}
						</Text>
						<Text fontSize="md" fontWeight="800" mt={1}>
							{item.title}
						</Text>
						<Text fontSize="sm" lineHeight="1.65" mt={1} opacity={0.78}>
							{item.text}
						</Text>
					</Box>
				</HStack>
			))}
		</VStack>
		<Divider borderColor={rt.ink === '#FFFFFF' ? 'whiteAlpha.300' : 'blackAlpha.300'} />
		<Box>
			<Text fontSize="xs" fontWeight="800" textTransform="uppercase" opacity={0.62}>
				Interview prompts
			</Text>
			<VStack align="stretch" spacing={2.5} mt={3}>
				{interviewAngles.map((angle, index) => (
					<HStack key={angle} align="start" spacing={3}>
						<Flex align="center" justify="center" w="22px" h="22px" borderRadius="6px" bg={rt.ink === '#FFFFFF' ? 'whiteAlpha.200' : 'blackAlpha.100'} flex="0 0 auto">
							<Text fontSize="11px" fontWeight="900" lineHeight="1">
								{index + 1}
							</Text>
						</Flex>
						<Text fontSize="sm" lineHeight="1.6" opacity={0.82}>
							{angle}
						</Text>
					</HStack>
				))}
			</VStack>
		</Box>
	</VStack>
)

const ContactPanel = ({ rt, copied, onCopy }) => (
	<VStack align="stretch" spacing={5} p={{ base: 5, md: 6 }} bg={rt.panelStrong} border="1px solid" borderColor={rt.line} borderRadius="8px" minH="100%">
		<Box>
			<Text color={rt.gold} fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em">
				Next step
			</Text>
			<Heading color={rt.text} fontSize={{ base: '2xl', md: '3xl' }} lineHeight="1.08" mt={2}>
				Resume, inbox, profiles.
			</Heading>
		</Box>
		<SimpleGrid columns={{ base: 2, sm: 3 }} gap={3}>
			<Button as={Link} href={resumeUrl} download leftIcon={<FiDownload />} borderRadius="8px" w="100%" bg={rt.text} color={rt.ink} _hover={{ bg: rt.gold, textDecoration: 'none' }}>
				Download PDF
			</Button>
			<Button as={Link} href={`mailto:${contactEmail}`} leftIcon={<FiMail />} borderRadius="8px" w="100%" bg={rt.panelMuted} color={rt.text} border="1px solid" borderColor={rt.line} _hover={{ borderColor: rt.teal, textDecoration: 'none' }}>
				Email
			</Button>
			<Box gridColumn={{ base: 'span 2', sm: 'auto' }}>
				<CopyEmailButton copied={copied} onCopy={onCopy} rt={rt} />
			</Box>
		</SimpleGrid>
		<VStack align="stretch" spacing={0} borderTop="1px solid" borderColor={rt.line}>
			<ContactRow icon={FiMail} label="Email" value={contactEmail} href={`mailto:${contactEmail}`} rt={rt} />
			<ContactRow icon={FiPhone} label="Phone" value={contactPhone} href={`tel:${contactPhone.replace(/-/g, '')}`} rt={rt} />
			<ContactRow icon={FiLinkedin} label="LinkedIn" value="linkedin.com/in/prasoonsoni" href={linkedin} rt={rt} />
			<ContactRow icon={FiGithub} label="GitHub" value="github.com/prasoonsoni" href={github} rt={rt} />
			<ContactRow icon={SiLeetcode} label="LeetCode" value="leetcode.com/prasoonsoni" href={leetcode} rt={rt} />
		</VStack>
	</VStack>
)

const ContactRow = ({ icon, label, value, href, rt }) => {
	const row = (
		<Flex align="center" justify="space-between" gap={4} py={4} borderBottom="1px solid" borderColor={rt.line}>
			<HStack spacing={3} minW="0">
				<Icon as={icon} color={rt.textMuted} flex="0 0 auto" />
				<Box minW="0">
					<Text color={rt.textMuted} fontSize="xs" fontWeight="bold" textTransform="uppercase">
						{label}
					</Text>
					<Text color={rt.text} fontSize="sm" fontWeight="semibold" noOfLines={1}>
						{value}
					</Text>
				</Box>
			</HStack>
			<Icon as={FiArrowUpRight} color={rt.textMuted} flex="0 0 auto" />
		</Flex>
	)

	if (!href) return row

	return (
		<Link href={href} target={href.startsWith('mailto:') ? undefined : '_blank'} _hover={{ textDecoration: 'none' }}>
			{row}
		</Link>
	)
}

export default RecruiterMode
